import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import BackToTop from '@/components/feature/BackToTop';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '赛事分类', path: `/category/${article.categorySlug}` },
    { name: article.category, path: `/category/${article.categorySlug}` },
    { name: article.title },
  ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      author: { '@type': 'Person', name: article.author },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '17c.cc每日大赛',
      },
      url: `https://17c.cc/article/${article.id}`,
      articleSection: article.category,
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path ? `https://17c.cc${item.path}` : undefined,
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} - 17c.cc每日大赛`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://17c.cc/article/${article.id}`}
        ogImage={article.image}
        ogType="article"
        schema={schema}
      />
      <Navbar />
      <BackToTop />

      <main className="pt-16 bg-background-50">
        <div className="px-4 md:px-6 max-w-4xl mx-auto pt-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="px-4 md:px-6 max-w-4xl mx-auto py-8 md:py-12">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-medium text-xs">
                {article.category}
              </span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
              <span>·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
          </header>

          {/* Featured Image */}
          <div className="rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-foreground max-w-none mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-background-200/70">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-sm">
                {tag}
              </span>
            ))}
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
                相关<span className="text-primary-600">文章</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-44 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{rel.category}</span>
                        <span>{rel.date}</span>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}